<script setup lang="ts">
import { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { AppStandardModal } from '@libs/ui';

import type { HotelFilterUpdate } from './model/hotelFilters';
import HotelFilters from './HotelFilters.vue';

interface HotelFiltersModalProps {
  headerText: string;
  ingredient: HotelIngredient | OwnStayIngredient | null;
  disableHotelNameFilter?: boolean;
}

const props = defineProps<HotelFiltersModalProps>();

interface HotelFiltersModalEmits {
  (eventName: 'close', origin: CloseHotelSelectionFiltersOrigin): void;
  (eventName: 'update-filter', form: HotelFilterUpdate): void;
}

const emit = defineEmits<HotelFiltersModalEmits>();

function onUpdateFilter(filterUpdate: HotelFilterUpdate): void {
  emit('update-filter', filterUpdate);
}
</script>

<template>
  <AppStandardModal
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('close', CloseHotelSelectionFiltersOrigin.CloseButton)"
  >
    <template #title>
      {{ props.headerText }}
    </template>

    <template #body>
      <div class="hotel-selection-filters-modal">
        <HotelFilters
          theme="dark"
          :ingredient="props.ingredient"
          :max-length="25"
          :disable-hotel-name-filter="props.disableHotelNameFilter"
          @update-filter="onUpdateFilter"
        />
      </div>
    </template>
    <template #summary>
      <div class="footer-wrapper">
        <slot name="footer" />
      </div>
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.footer-wrapper {
  width: 100%;
  position: sticky;
  bottom: 0;
  left: 0;

  background: var(--color-white);

  z-index: 1;

  @include for-less-or-equal-than-900-px {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: var(--spacing-16) var(--spacing-16) var(--spacing-32)
      var(--spacing-16);
    width: calc(100% - var(--spacing-16) - var(--spacing-16));
    background: var(--color-white);
    border-top: 1px solid var(--color-neutral-30);
  }
}
.hotel-selection-filters-modal {
  position: relative;

  @include for-less-or-equal-than-900-px {
    // NOTE: Padding to accommodate for footer
    padding-bottom: 70px;
    position: relative;

    .footer-wrapper {
      position: fixed;
      bottom: 0;
      left: 0;
      padding: var(--spacing-16) var(--spacing-16) var(--spacing-32)
        var(--spacing-16);
      width: calc(100% - var(--spacing-16) - var(--spacing-16));
      background: var(--color-white);
      border-top: 1px solid var(--color-neutral-30);
    }
  }
}
</style>
